"use client";

import { useState, useRef } from "react";
import { Play, Pause } from "lucide-react";

export interface VoiceMessagePlayerProps {
  src: string;
  fileType?: string;
}

export function VoiceMessagePlayer({ src, fileType }: VoiceMessagePlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const animRef = useRef<number>(null);

  const speeds = [1, 1.5, 2];

  const bars = useRef(
    Array.from({ length: 36 }, (_, i) => {
      const seed = (i * 7 + 13) % 17;
      return 4 + seed * 1.6;
    })
  ).current;

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      if (animRef.current) cancelAnimationFrame(animRef.current);
    } else {
      audioRef.current.playbackRate = speed;
      audioRef.current.play();
      const tick = () => {
        if (!audioRef.current) return;
        setProgress(audioRef.current.currentTime / (audioRef.current.duration || 1));
        animRef.current = requestAnimationFrame(tick);
      };
      tick();
    }
    setPlaying(!playing);
  };

  const cycleSpeed = () => {
    const nextIdx = (speeds.indexOf(speed) + 1) % speeds.length;
    const next = speeds[nextIdx];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return "0:00";
    return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  return (
    <div className="mt-2 max-w-[320px]">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => { setPlaying(false); setProgress(0); if (animRef.current) cancelAnimationFrame(animRef.current); }}
      />
      {/* Uses --primary CSS var from enterprise branding, falls back to blue */}
      <div
        className="flex items-center gap-2.5 rounded-2xl px-3 py-2.5 shadow-lg"
        style={{ background: 'var(--primary, #3b82f6)' }}
      >
        {/* Play/Pause */}
        <button
          onClick={toggle}
          className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all active:scale-90 flex-shrink-0"
        >
          {playing ? (
            <Pause className="w-4 h-4 text-white fill-white" />
          ) : (
            <Play className="w-4 h-4 text-white fill-white ml-0.5" />
          )}
        </button>

        {/* Waveform */}
        <div className="flex items-center gap-[2px] flex-1 h-8">
          {bars.map((h, i) => {
            const played = i / bars.length <= progress;
            return (
              <div
                key={i}
                className={`w-[2.5px] rounded-full transition-all duration-100 ${
                  played ? "bg-white" : "bg-white/25"
                } ${playing && played ? "animate-pulse" : ""}`}
                style={{ height: `${h}px` }}
              />
            );
          })}
        </div>

        {/* Speed toggle */}
        <button
          onClick={cycleSpeed}
          className="min-w-[32px] h-6 rounded-full bg-white/20 hover:bg-white/30 text-[10px] font-bold text-white transition-colors flex-shrink-0"
        >
          {speed}x
        </button>

        {/* Duration */}
        <span className="text-[11px] font-mono text-white/80 min-w-[38px] text-right flex-shrink-0">
          {playing ? fmt(audioRef.current?.currentTime || 0) : fmt(duration)}
        </span>
      </div>
    </div>
  );
}
